import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { type IMainCarouselItem } from '../../api/personas'
import { type ContextByIdResponse } from '../../api/session'
import type { MentorBase, SessionMentor } from '../../types/mentor'
import { Persona } from '../../types/persona'
import apiBase from '../../utils/baseApi'
import {
  CompanionUpdateInput,
  fetchMentorsParams,
  FilterOptions,
  IGenerateMentorRequest,
  IGenerateMentorResponse,
  JobTitlesResponse,
  MentorsResponse,
  Profile,
} from './types'

export const JOB_TITLES_PER_PAGE = 300
export const MENTORS_PER_PAGE = 102

async function fetchMentors({
  pageNumber,
  filters = {},
  sortBy = '',
  order = '',
}: fetchMentorsParams): Promise<MentorsResponse> {
  try {
    const queryParams = new URLSearchParams({
      ...(filters.gender && { gender: filters.gender }),
      ...(filters.race && { race: filters.race }),
      ...(filters.industry && { industry: filters.industry }),
      ...(filters.jobTitle && { jobTitle: filters.jobTitle }),
      ...(filters.department && { department: filters.department }),
      ...(filters.company && { company: filters.company }),
      ...(filters.search && { search: filters.search }),
      ...(filters.search_mode && { search_mode: filters.search_mode }),
      ...(sortBy && { sort_by: sortBy }),
      ...(order && { sort_order: order }),
    })
    const pageIndex = pageNumber > 0 ? pageNumber - 1 : 0
    const res = await apiBase.get<MentorsResponse>(
      `/v2/admin/mentors?page_size=${MENTORS_PER_PAGE}&page_index=${pageIndex}&${queryParams}`,
    )
    return res.data
  } catch (error) {
    console.error('Error fetching mentors:', error)
    throw error
  }
}

async function fetchFilterOptions(): Promise<FilterOptions> {
  try {
    const response = await apiBase.get<FilterOptions>('/v1/get-filters')
    return response.data
  } catch (error) {
    console.error('Error fetching filter options:', error)
    throw error
  }
}

async function getJobTitles(
  pageNumber: number,
  search: string,
): Promise<JobTitlesResponse> {
  try {
    const response = await apiBase.get<JobTitlesResponse>(
      `/v2/mentors/job-titles?page_size=${JOB_TITLES_PER_PAGE}&page_index=${pageNumber}&search=${search}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching job titles:', error)
    throw error
  }
}

async function getCompanies(
  pageNumber: number,
  search: string,
): Promise<JobTitlesResponse> {
  try {
    const response = await apiBase.get<JobTitlesResponse>(
      `/v2/company-names?page_size=${JOB_TITLES_PER_PAGE}&page_index=${pageNumber}&search=${search}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching companies:', error)
    throw error
  }
}

async function getDepartments(
  pageNumber: number,
  search: string,
): Promise<JobTitlesResponse> {
  try {
    const response = await apiBase.get<JobTitlesResponse>(
      `/v2/mentor-departments?page_size=${JOB_TITLES_PER_PAGE}&page_index=${pageNumber}&search=${search}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching departments:', error)
    throw error
  }
}

export const generateMentor = async (
  data: IGenerateMentorRequest,
): Promise<IGenerateMentorResponse> => {
  try {
    const response = await apiBase.post<IGenerateMentorResponse>(
      '/v2/admin/mentors',
      { ...data, project: 'companion' },
    )
    return response.data
  } catch (error) {
    console.error('Error generating companion:', error)
    throw error
  }
}

export async function fetchPersonaById(id: string): Promise<Persona> {
  try {
    const response = await apiBase.get<Persona>(`/v1/persona/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching persona by id:', error)
    throw error
  }
}

export const approveMentor = async (id: string): Promise<void> => {
  try {
    const response = await apiBase.patch<void>(`/v2/admin/mentors/${id}`)
    return response.data
  } catch (error) {
    console.error('Error approving mentor:', error)
    throw error
  }
}

interface StartUploadImageResponse {
  uploadUrl: string
}

export const startUploadImage = async (
  file: string,
  type: 'temp' | 'marketing' | 'user-consent' = 'temp',
): Promise<StartUploadImageResponse> => {
  try {
    const response = await apiBase.post<StartUploadImageResponse>(
      '/v2/admin/uploads',
      {
        originalFileName: file,
        type,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error starting file upload:', error)
    throw error
  }
}

export const deleteMentor = async (
  id: string,
): Promise<{
  success: true
}> => {
  try {
    const response = await apiBase.delete<{
      success: true
    }>(`/v2/admin/mentors/${id}`)
    return response.data
  } catch (error) {
    console.error('Error deleting mentor:', error)
    throw error
  }
}

interface UploadImageResponse {
  config: {
    url?: string
  }
}

export const uploadImage = async (
  file: File,
  type?: 'temp' | 'marketing' | 'user-consent',
): Promise<UploadImageResponse> => {
  const { uploadUrl } = await startUploadImage(file.name, type)
  const response = await axios.put<UploadImageResponse>(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
  })
  if (response.status !== 201 && response.status !== 200) {
    throw new Error('Failed to upload file')
  }
  return response
}

export interface Prompts {
  id: string
  pk: string
  hpk: string
  name: string
  description?: string
  system_prompt?: string
  user_prompt?: string
  response_format?: Record<string, unknown>
  extra_context?: string
  updated_at: number
}

export const getAllGenerationPrompts = async () => {
  try {
    const response = await apiBase.get<{
      success: boolean
      data: Prompts[]
    }>('/v2/admin/generation-prompts')
    return response.data
  } catch (error) {
    console.error('Error fetching generation prompts:', error)
    throw error
  }
}

export const getGenerationPromptsById = async (id: string) => {
  try {
    const response = await apiBase.get<{
      success: boolean
      data: Prompts
    }>(`/v2/admin/generation-prompts/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching generation prompt by id:', error)
    throw error
  }
}

export type Nullish<T> = { [P in keyof T]?: T[P] | undefined | null }
export const updateGenerationPromptById = async (
  id: string,
  data: Nullish<Prompts>,
) => {
  try {
    const response = await apiBase.put<{
      success: boolean
      data: Prompts
    }>(`/v2/admin/generation-prompts/${id}`, data)
    return response.data
  } catch (error) {
    console.error('Error updating generation prompt by id:', error)
    throw error
  }
}

interface UpdateCarouselDataParams {
  items: IMainCarouselItem[]
  updatedAt?: number
}

export const updateCarouselData = async (data: UpdateCarouselDataParams) => {
  try {
    const response = await apiBase.patch<{
      success: boolean
      message: string
    }>('/v2/admin/carousels', {
      data,
    })
    return response.data
  } catch (error) {
    console.error('Error updating carousel data:', error)
    throw error
  }
}

export interface MentorsSearchParams {
  search: string
  size: number
}

export const universalMentorSearch = async (
  params: MentorsSearchParams,
): Promise<MentorBase[] | undefined> => {
  try {
    const response = await apiBase.get<MentorBase[] | undefined>(
      '/v2/mentors/draft-search',
      {
        params: {
          search: params.search,
          size: params.size,
        },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error searching mentors:', error)
    throw error
  }
}

export async function getContextById(id: string): Promise<ContextByIdResponse> {
  const answerResponse = await apiBase.get<ContextByIdResponse>(
    `/v2/contexts/${id}`,
  )

  return answerResponse.data
}

interface MentorProfileBySlugResponse {
  data: SessionMentor
  success: boolean
}

export async function fetchMentorProfileBySlug(
  slug: string,
): Promise<MentorProfileBySlugResponse> {
  try {
    const response = await apiBase.get<MentorProfileBySlugResponse>(
      `/v2/mentors/slug/${slug}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching companion profile by slug:', error)
    throw error
  }
}

export const uploadMarketingMedia = async (file: File) => {
  const { uploadUrl } = await startUploadImage(file.name, 'marketing')
  const response = await axios.put<UploadImageResponse>(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
  })
  if (response.status !== 201 && response.status !== 200) {
    throw new Error('Failed to upload file')
  }
  return response.config?.url?.split('?')[0]
}

interface UpdateContextByIdParams {
  id: string
  title?: string
  imageUrl?: string
  prompt?: string
  outline?: string
  clickbaitHeader?: string
}

interface UpdateContextByIdResponse {
  success: boolean
  id: string
  url: string
}

export const updateContextById = async ({
  id,
  ...data
}: UpdateContextByIdParams): Promise<UpdateContextByIdResponse> => {
  try {
    const response = await apiBase.patch<UpdateContextByIdResponse>(
      `/v2/admin/contexts/${id}`,
      data,
    )
    return response.data
  } catch (error) {
    console.error('Error updating context by id:', error)
    throw error
  }
}

interface GenerateMarketingImageResponse {
  success: boolean
  imageUrl: string
}

export const generateMarketingImage = async (
  query: string,
): Promise<GenerateMarketingImageResponse> => {
  try {
    if (!query?.trim()) {
      throw new Error('Query parameter is required and cannot be empty')
    }

    const response = await apiBase.get<GenerateMarketingImageResponse>(
      '/v2/admin/marketing/images',
      {
        params: { query: query.trim() },
      },
    )

    if (!response.data.imageUrl?.trim()) {
      throw new Error('Invalid response: imageUrl is missing or empty')
    }

    return response.data
  } catch (error) {
    console.error('Error generating marketing image:', error)
    throw error
  }
}

export async function updateCompanion({
  mentorId,
  ...data
}: CompanionUpdateInput) {
  try {
    const response = await apiBase.patch<{
      updateMentor?: { success: boolean }
    }>(`/v2/mentors/${mentorId}`, {
      mentorData: data,
    })
    return response.data
  } catch (error) {
    console.error('Error updating companion:', error)
    throw error
  }
}

// react-query hooks
export const useAllMentors = (params: fetchMentorsParams) =>
  useQuery({
    queryKey: ['get-all-mentors', { ...params }],
    queryFn: () => fetchMentors(params),
    placeholderData: prev => prev,
  })

export const useFetchFilters = () =>
  useQuery({
    queryKey: ['get-all-filters'],
    queryFn: fetchFilterOptions,
    enabled: true,
  })

export const useFetchJobTitles = (search: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['get-job-titles', search],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getJobTitles(pageParam, search),
    getNextPageParam: data =>
      data.page_index + 1 >= data.total_page ? undefined : data.page_index + 1,
    placeholderData: prev => prev,
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

export const useFetchCompanies = (search: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['get-companies', search],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getCompanies(pageParam, search),
    getNextPageParam: data =>
      data.page_index + 1 >= data.total_page ? undefined : data.page_index + 1,
    placeholderData: prev => prev,
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

export const useFetchDepartments = (search: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['get-departments', search],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getDepartments(pageParam, search),
    getNextPageParam: data =>
      data.page_index + 1 >= data.total_page ? undefined : data.page_index + 1,
    placeholderData: prev => prev,
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

export const useGenerateMentor = () =>
  useMutation({ mutationFn: generateMentor })

export const useFetchPersonaById = (id: string, enabled: boolean) =>
  useQuery<
    {
      data: {
        profile: {
          profile: Profile
        }
      }
    },
    AxiosError
  >({
    queryKey: ['get-persona-by-id', id],
    queryFn: () => fetchPersonaById(id),
    enabled: !!id,
    refetchInterval: enabled ? 10000 : false,
    refetchIntervalInBackground: true,
  })

export const useApproveMentor = () => useMutation({ mutationFn: approveMentor })

export const useDeleteMentor = () => useMutation({ mutationFn: deleteMentor })

export const useFetchAllGenerationPrompts = () =>
  useQuery({
    queryKey: ['get-all-generation-prompts'],
    queryFn: getAllGenerationPrompts,
  })

export const useFetchGenerationPromptById = (id: string) =>
  useQuery({
    queryKey: ['get-generation-prompt-by-id', id],
    queryFn: () => getGenerationPromptsById(id),
    enabled: !!id,
  })

export const useUpdateGenerationPromptById = (id: string) =>
  useMutation({
    mutationFn: (data: Nullish<Prompts>) =>
      updateGenerationPromptById(id, data),
  })

export const useUpdateCarouselData = () =>
  useMutation({
    mutationFn: (data: { items: IMainCarouselItem[]; updated_at?: number }) =>
      updateCarouselData(data),
  })

export const useUpdateContextById = () =>
  useMutation({
    mutationFn: (data: UpdateContextByIdParams) => updateContextById(data),
  })

export const useGenerateMarketingImage = (query: string, enabled: boolean) =>
  useQuery({
    queryKey: ['generate-marketing-image', query],
    queryFn: () => generateMarketingImage(query),
    enabled: !!query && enabled,
  })

export const useUpdateCompanion = () =>
  useMutation({
    mutationFn: (data: CompanionUpdateInput) => updateCompanion(data),
  })
